import { z } from 'zod'
import { LIBERAL_JOB_ID, MEDICAL_JOB_ID, RETIRED_JOB_ID } from '@/app/constants/loanTypes'

// Custom validators
const isValidAge = (dateString: string): { isValid: boolean; message?: string } => {
    if (!dateString || !dateString.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
        return { isValid: false, message: 'Merci de vérifier votre date de naissance.' }
    }

    const [day, month, year] = dateString.split('/').map(Number)
    const birthDate = new Date(year, month - 1, day)
    const today = new Date()
    const age = today.getFullYear() - birthDate.getFullYear()
    const monthDiff = today.getMonth() - birthDate.getMonth()

    const actualAge = monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate()) ? age - 1 : age

    if (actualAge < 18) {
        return { isValid: false, message: 'Tout emprunteur doit avoir au minimum 18 ans.' }
    }
    if (actualAge > 90) {
        return { isValid: false, message: 'Tout emprunteur doit avoir au maximum 90 ans.' }
    }

    return { isValid: true }
}

// Base schemas - reusable components
const birthdateSchema = z
    .string({ required_error: 'La date de naissance est requise.' })
    .min(1, 'Veuillez indiquer la date de naissance.')
    .regex(/^\d{2}\/\d{2}\/\d{4}$/, 'Format de date invalide (JJ/MM/AAAA).')
    // Zod v4 n'accepte plus de fonction en 2e argument de .refine : le message d'âge partait en « Invalid input »
    .superRefine((date, ctx) => {
        const { isValid, message } = isValidAge(date)
        if (!isValid) ctx.addIssue({ code: 'custom', message: message || 'Date invalide' })
    })

const jobIdSchema = z
    .string({ required_error: 'La profession est requise.' })
    .min(1, 'Veuillez sélectionner la profession exercée.')

const coveragePercentageSchema = z
    .number({
        required_error: 'Le pourcentage de couverture est requis.',
        invalid_type_error: 'Le pourcentage doit être un nombre.',
    })
    .min(1, 'Le pourcentage de couverture est requis.')
    .max(100, 'Le pourcentage ne peut pas dépasser 100%.')
    .int('Le pourcentage doit être un nombre entier.')

const booleanRiskSchema = z.boolean().default(false)

// Coerce string "true"/"false" to boolean (for radio buttons)
const booleanFromRadioSchema = z
    .union([z.boolean(), z.literal('true'), z.literal('false')])
    .transform((val) => val === true || val === 'true')
    .default(false)

const optionalBooleanFromRadioSchema = z
    .union([z.boolean(), z.literal('true'), z.literal('false')])
    .transform((val) => val === true || val === 'true')
    .optional()

// Borrower form schema - composed from base schemas
// Note: cover_percentage is managed in guarantees.schema.ts (LoanComparatorBorrowerGuaranteesForm)
export const borrowerFormSchema = z
    .object({
        birthdate: birthdateSchema,
        job_id: jobIdSchema,
        smoker: booleanFromRadioSchema,
        medical_risk: booleanFromRadioSchema,
        job_risk: booleanFromRadioSchema,
        risky_sport: booleanFromRadioSchema,

        // Conditional fields
        liberal_job: z.string().optional(),
        medical_job: z.string().optional(),
        job_risk_field: z.string().optional(),
        risky_sport_name: z.string().optional(),
        job_clerk: optionalBooleanFromRadioSchema,
        searching_job: optionalBooleanFromRadioSchema,
        job_handler: optionalBooleanFromRadioSchema,
        job_trips: optionalBooleanFromRadioSchema, // Professional travel
        working_height: z.number().optional(),
        annual_mileage: z.number().optional(),
        rejected_by_insurance: optionalBooleanFromRadioSchema,
        address_country: z.string().min(1, 'Merci de renseigner votre lieu de résidence.'),
    })
    // Parité front Vue : le secteur d'activité devient obligatoire dès que le métier est déclaré à risques
    .superRefine((data, ctx) => {
        if (data.job_risk && !data.job_risk_field) {
            ctx.addIssue({
                code: 'custom',
                path: ['job_risk_field'],
                message: "Veuillez sélectionner un secteur d'activité à risques.",
            })
        }
    })
    .superRefine((data, ctx) => {
        if (data.risky_sport && !data.risky_sport_name) {
            ctx.addIssue({
                code: 'custom',
                path: ['risky_sport_name'],
                message: 'Veuillez sélectionner le sport pratiqué.',
            })
        }
    })
    // Parité front Vue et règle back (isValidateProfession) : la sous-profession est obligatoire pour un
    // libéral ou un retraité, et la spécialité pour une profession médicale. Sans elle, le back rejette
    // le devis en 422 et les tarificateurs (Utwin, BPSI) ne savent pas classer l'emprunteur.
    .superRefine((data, ctx) => {
        const jobId = parseInt(data.job_id)
        if ((jobId === LIBERAL_JOB_ID || jobId === RETIRED_JOB_ID) && !data.liberal_job) {
            ctx.addIssue({
                code: 'custom',
                path: ['liberal_job'],
                message:
                    jobId === RETIRED_JOB_ID
                        ? 'Veuillez indiquer la profession exercée dernièrement.'
                        : 'Veuillez préciser votre type de profession libérale.',
            })
        }
        if (jobId === LIBERAL_JOB_ID && parseInt(data.liberal_job ?? '') === MEDICAL_JOB_ID && !data.medical_job) {
            ctx.addIssue({
                code: 'custom',
                path: ['medical_job'],
                message: 'Veuillez choisir votre type de profession médicale.',
            })
        }
    })

// Infer TypeScript type from schema
export type BorrowerFormData = z.infer<typeof borrowerFormSchema>

// Default values factory
export const createBorrowerFormDefaults = (): BorrowerFormData => ({
    birthdate: '',
    job_id: '',
    smoker: false,
    medical_risk: false,
    job_risk: false,
    risky_sport: false,
    address_country: 'FRANCE', // Default to France

    // Conditional fields defaults
    liberal_job: undefined,
    medical_job: undefined,
    job_risk_field: undefined,
    risky_sport_name: undefined,
    job_clerk: undefined,
    searching_job: undefined,
    job_handler: undefined,
    job_trips: undefined,
    working_height: undefined,
    annual_mileage: undefined,
    rejected_by_insurance: undefined,
})

// Safe parse with detailed error handling
export const parseBorrowerForm = (data: unknown) => {
    return borrowerFormSchema.safeParse(data)
}

// Export validators for individual fields (useful for partial validation)
export const borrowerValidators = {
    birthdate: birthdateSchema,
    job_id: jobIdSchema,
    coveragePercentage: coveragePercentageSchema,
    risk: booleanRiskSchema,
} as const
